import {
  AccessLevel,
  LEGACY_SPECIAL_ACCESS_METADATA,
  ORBCON_ADMIN_DOMAIN,
} from '../types/access';

export function getEmailDomain(email: string): string | null {
  const parts = email.split('@');
  if (parts.length !== 2) return null;
  return parts[1].toLowerCase();
}

export function isOntarioHospitalDomain(
  email: string,
  hospitalDomains: readonly string[]
): boolean {
  const domain = getEmailDomain(email);
  if (!domain) return false;
  return hospitalDomains.includes(domain);
}

export function isOrbconAdminDomain(email: string): boolean {
  return getEmailDomain(email) === ORBCON_ADMIN_DOMAIN;
}

function metadataIsSpecialAccess(value: unknown): boolean {
  return value === AccessLevel.SpecialAccess || value === LEGACY_SPECIAL_ACCESS_METADATA;
}

export function determineAccessLevelFromEmails(
  emails: Array<{ address: string | null | undefined; verified: boolean }>,
  metadata: {
    accessLevel?: string;
  } | undefined,
  hospitalDomains: readonly string[]
): AccessLevel {
  const verified = emails
    .filter((entry) => entry.verified && entry.address)
    .map((entry) => entry.address as string);

  if (verified.some((address) => isOrbconAdminDomain(address))) {
    return AccessLevel.Admin;
  }

  if (metadataIsSpecialAccess(metadata?.accessLevel)) {
    return AccessLevel.SpecialAccess;
  }

  if (verified.some((address) => isOntarioHospitalDomain(address, hospitalDomains))) {
    return AccessLevel.OntarioHealthcareProfessionals;
  }

  return AccessLevel.Public;
}

/**
 * Resolve access for a signed-in user.
 * Admin is verified @orbcon.ca only (not Clerk isAdmin metadata).
 * Unverified hospital / orbcon emails stay Public until they confirm the inbox.
 */
export function determineAccessLevel(
  email: string | null | undefined,
  emailVerified: boolean,
  metadata: {
    accessLevel?: string;
  } | undefined,
  hospitalDomains: readonly string[]
): AccessLevel {
  return determineAccessLevelFromEmails(
    [{ address: email, verified: emailVerified }],
    metadata,
    hospitalDomains
  );
}

export function canViewDashboard(isAuthenticated: boolean): boolean {
  return isAuthenticated;
}

export function canViewElearning(isAuthenticated: boolean): boolean {
  return isAuthenticated;
}

export function canViewData(accessLevel: AccessLevel | null): boolean {
  return (
    accessLevel === AccessLevel.OntarioHealthcareProfessionals ||
    accessLevel === AccessLevel.SpecialAccess ||
    accessLevel === AccessLevel.Admin
  );
}

export function canEnterData(accessLevel: AccessLevel | null): boolean {
  return accessLevel === AccessLevel.SpecialAccess || accessLevel === AccessLevel.Admin;
}

export function canAccessAdmin(accessLevel: AccessLevel | null): boolean {
  return accessLevel === AccessLevel.Admin;
}

export function getAccessLevelDisplayName(level: AccessLevel | null): string {
  if (!level) return 'Visitor';
  const names: Record<AccessLevel, string> = {
    [AccessLevel.Public]: 'Public',
    [AccessLevel.OntarioHealthcareProfessionals]: 'Ontario Healthcare Professional',
    [AccessLevel.SpecialAccess]: 'Special access',
    [AccessLevel.Admin]: 'Administrator',
  };
  return names[level];
}
